import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import { categories, getArticlesByCategory } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'shishang');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = categories.find(c => c.slug === activeSlug);
  const articles = getArticlesByCategory(activeSlug);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Category Header */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">{currentCategory?.name || '分类'}</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              {currentCategory?.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-600">
              {currentCategory?.description}
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 z-30 bg-background-50 border-b border-background-200/60">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-3 overflow-x-auto flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            {articles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-100 rounded-xl overflow-hidden hover:scale-[1.02] transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 whitespace-nowrap">
                          {currentCategory?.name}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 md:p-5">
                      <h2 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <div className="flex items-center gap-3">
                          <span>{article.publishDate}</span>
                          <span>{article.readTime} 阅读</span>
                        </div>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {article.views.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory?.name} - 尤物视频`,
            description: currentCategory?.description,
            url: `https://glamzone3.github.io/category/${activeSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '尤物视频',
              url: 'https://glamzone3.github.io',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://glamzone3.github.io',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: currentCategory?.name,
                  item: `https://glamzone3.github.io/category/${activeSlug}`,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}